import { forwardRef, ReactNode, useEffect, useRef, useState } from 'react'
import cx from 'classnames'

import { CloseSquare as CloseSquareIcon } from 'common/icons'

export type TimePickerProps = {
  /** HH:mm string or null */
  value?: string | null
  defaultValue?: string | null
  onChange?: (value: string | null) => void
  disabled?: boolean
  allowClear?: boolean
  format?: string
  minuteStep?: number
  placeholder?: string
  className?: string
  style?: React.CSSProperties
  suffixIcon?: ReactNode
  'data-testid'?: string
  'data-cy'?: string
  // absorbed rc-time-picker / moment compat props (ignored)
  disabledHours?: unknown
  disabledMinutes?: unknown
  disabledSeconds?: unknown
  hideDisabledOptions?: unknown
  defaultOpenValue?: unknown
  showSecond?: unknown
}

const pad = (n: number) => String(n).padStart(2, '0')
const HOURS = Array.from({ length: 24 }, (_, i) => i)

const buildMinuteOptions = (minuteStep: number): number[] => {
  const options: number[] = []
  for (let m = 0; m < 60; m += minuteStep) {
    options.push(m)
  }
  return options
}

const triggerClass = cx(
  'w-full px-4 pr-10 bg-gold-40 h-10 rounded-sm font-normal text-base-700 text-sm outline-none border border-gold-400',
  'hover:border-gold-500 focus:border-base-700',
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gold-40'
)

type SteppedPickerProps = {
  value: string
  onChange?: (value: string | null) => void
  disabled?: boolean
  allowClear: boolean
  minuteStep: number
  placeholder?: string
  suffixIcon?: ReactNode
  className?: string
  style?: React.CSSProperties
  dataTestId?: string
  dataCy?: string
}

const SteppedTimePicker = ({
  value,
  onChange,
  disabled,
  allowClear,
  minuteStep,
  placeholder,
  suffixIcon,
  className,
  style,
  dataTestId,
  dataCy,
}: SteppedPickerProps) => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const hourColRef = useRef<HTMLDivElement>(null)
  const minuteColRef = useRef<HTMLDivElement>(null)

  const parsedHour = value ? parseInt(value.split(':')[0], 10) : null
  const parsedMinute = value ? parseInt(value.split(':')[1], 10) : null
  const minuteOptions = buildMinuteOptions(minuteStep)

  const scrollToSelected = () => {
    if (parsedHour !== null && hourColRef.current) {
      hourColRef.current
        .querySelector<HTMLElement>(`[data-value="${parsedHour}"]`)
        ?.scrollIntoView({ block: 'center' })
    }
    if (parsedMinute !== null && minuteColRef.current) {
      minuteColRef.current
        .querySelector<HTMLElement>(`[data-value="${parsedMinute}"]`)
        ?.scrollIntoView({ block: 'center' })
    }
  }

  useEffect(() => {
    if (!open) {
      return
    }
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const selectTime = (h: number | null, m: number | null) => {
    const finalH = h ?? parsedHour ?? 0
    const finalM = m ?? parsedMinute ?? 0
    onChange?.(`${pad(finalH)}:${pad(finalM)}`)
  }

  const hasValue = !!value
  const showClear = allowClear && hasValue && !disabled

  return (
    <div
      ref={containerRef}
      className={cx('relative flex items-center w-full', className)}
      style={style}
    >
      <button
        type="button"
        disabled={disabled}
        aria-label={placeholder}
        data-testid={dataTestId}
        data-cy={dataCy}
        onClick={() => {
          if (disabled) {
            return
          }
          setOpen((v) => {
            if (!v) {
              setTimeout(scrollToSelected, 0)
            }
            return !v
          })
        }}
        className={cx(
          triggerClass,
          'text-left cursor-pointer',
          hasValue ? 'text-base-700' : 'text-gold-500'
        )}
      >
        {value || placeholder || '--:--'}
      </button>

      {showClear ? (
        <button
          type="button"
          className="absolute right-3 text-gold-700 hover:text-base-700"
          onClick={() => onChange?.(null)}
        >
          <CloseSquareIcon isDefaultColor={false} size={4} />
        </button>
      ) : suffixIcon ? (
        <span className="absolute right-3 pointer-events-none text-gold-700">
          {suffixIcon}
        </span>
      ) : null}

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gold-300 rounded shadow-lg flex overflow-hidden">
          <div ref={hourColRef} className="overflow-y-auto max-h-48 w-14">
            {HOURS.map((h) => (
              <div
                key={h}
                data-value={h}
                onMouseDown={(e) => {
                  e.preventDefault()
                  selectTime(h, null)
                }}
                className={cx(
                  'px-3 py-2 text-sm cursor-pointer text-center select-none',
                  h === parsedHour
                    ? 'bg-base-700 text-white'
                    : 'text-base-700 hover:bg-gold-100'
                )}
              >
                {pad(h)}
              </div>
            ))}
          </div>
          <div className="w-px bg-gold-200 flex-shrink-0" />
          <div ref={minuteColRef} className="overflow-y-auto max-h-48 w-14">
            {minuteOptions.map((m) => (
              <div
                key={m}
                data-value={m}
                onMouseDown={(e) => {
                  e.preventDefault()
                  selectTime(null, m)
                }}
                className={cx(
                  'px-3 py-2 text-sm cursor-pointer text-center select-none',
                  m === parsedMinute
                    ? 'bg-base-700 text-white'
                    : 'text-base-700 hover:bg-gold-100'
                )}
              >
                {pad(m)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const TimePicker = forwardRef<HTMLInputElement, TimePickerProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      disabled,
      allowClear = true,
      minuteStep,
      placeholder,
      suffixIcon,
      className,
      style,
      'data-testid': dataTestId,
      'data-cy': dataCy,
    },
    ref
  ) => {
    const timeString = value ?? ''
    const hasValue = !!timeString

    if (minuteStep) {
      return (
        <SteppedTimePicker
          value={timeString}
          onChange={onChange}
          disabled={disabled}
          allowClear={allowClear}
          minuteStep={minuteStep}
          placeholder={placeholder}
          suffixIcon={suffixIcon}
          className={className}
          style={style}
          dataTestId={dataTestId}
          dataCy={dataCy}
        />
      )
    }

    const defaultTimeString = defaultValue ?? undefined
    const showClear = allowClear && hasValue && !disabled
    const hasSuffix = !!suffixIcon

    return (
      <div
        className={cx('relative flex items-center w-full', className)}
        style={style}
      >
        <input
          ref={ref}
          type="time"
          value={timeString}
          defaultValue={defaultTimeString}
          disabled={disabled}
          placeholder={placeholder}
          data-testid={dataTestId}
          data-cy={dataCy}
          onClick={(e) => {
            try {
              ;(e.currentTarget as HTMLInputElement).showPicker()
            } catch {
              // showPicker not supported in all browsers
            }
          }}
          onChange={(e) => {
            const val = e.target.value
            onChange?.(val || null)
          }}
          className={cx(
            triggerClass,
            showClear || hasSuffix
              ? '[&::-webkit-calendar-picker-indicator]:hidden'
              : '[&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:cursor-pointer'
          )}
        />
        {showClear ? (
          <button
            type="button"
            className="absolute right-3 text-gold-700 hover:text-base-700"
            onClick={() => onChange?.(null)}
          >
            <CloseSquareIcon isDefaultColor={false} size={4} />
          </button>
        ) : hasSuffix ? (
          <span className="absolute right-3 pointer-events-none text-gold-700">
            {suffixIcon}
          </span>
        ) : null}
      </div>
    )
  }
)

export default TimePicker
